import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StadiumOutlinedIcon from "@mui/icons-material/StadiumOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { BadmintonMarkIcon } from "@bw/material";
import type { AccessEvaluator, NavDefinition } from "@bw/shared/navigation";

/**
 * The one platform navigation source of truth (direction B, modes). The whole
 * app is already gated by PlatformAccessGuard, so pages carry no access hint.
 */
export const platformNavigation: NavDefinition<never> = {
  app: "platform",
  homePageId: "home",
  modes: [
    {
      id: "home",
      label: "Home",
      icon: HomeOutlinedIcon,
      iconOnly: true,
      pages: [
        {
          id: "home",
          label: "Home",
          path: "/",
          icon: HomeOutlinedIcon,
          keywords: ["dashboard"],
        },
      ],
    },
    {
      id: "review",
      label: "Review",
      icon: InboxOutlinedIcon,
      pages: [
        {
          id: "approvals",
          label: "Approvals",
          path: "/approvals",
          icon: FactCheckOutlinedIcon,
          keywords: ["club applications", "review"],
        },
        {
          id: "alerts",
          label: "Alerts",
          path: "/alerts",
          icon: NotificationsActiveOutlinedIcon,
        },
        {
          id: "coach-experience",
          label: "Coach experience",
          path: "/coach-experience",
          icon: BadmintonMarkIcon,
        },
      ],
    },
    {
      id: "clubs",
      label: "Clubs",
      icon: ApartmentOutlinedIcon,
      pages: [
        {
          id: "organizations",
          label: "Organizations",
          path: "/organizations",
          icon: ApartmentOutlinedIcon,
          keywords: ["clubs"],
        },
        {
          id: "onboarding",
          label: "Onboarding",
          path: "/onboarding",
          icon: RocketLaunchOutlinedIcon,
        },
        {
          id: "facilities",
          label: "Facilities & places",
          path: "/facilities",
          icon: StadiumOutlinedIcon,
          keywords: ["places", "venues", "facilities"],
        },
        {
          id: "sanctioning",
          label: "Sanctioning",
          path: "/sanctioning",
          icon: MilitaryTechOutlinedIcon,
          keywords: ["tournaments", "official", "ratings"],
          sections: [
            {
              id: "sanction-requests",
              label: "Requests",
              path: "/sanctioning",
              keywords: ["sanction requests", "review"],
            },
            {
              id: "sanctioned-events",
              label: "Sanctioned events",
              path: "/sanctioning/events",
              keywords: ["official events", "results", "apply ratings"],
            },
          ],
        },
      ],
    },
    {
      id: "people",
      label: "People",
      icon: GroupOutlinedIcon,
      pages: [
        {
          id: "users",
          label: "Users",
          path: "/users",
          icon: PersonOutlinedIcon,
          keywords: ["players", "people"],
        },
        {
          id: "moderation",
          label: "Moderation",
          path: "/moderation",
          icon: GavelOutlinedIcon,
          keywords: ["reports", "appeals", "bans"],
        },
      ],
    },
    {
      id: "money",
      label: "Money",
      icon: AccountBalanceOutlinedIcon,
      pages: [
        {
          id: "finance",
          label: "Finance",
          path: "/finance",
          icon: PaymentsOutlinedIcon,
          keywords: ["settlements", "reconciliation"],
        },
        {
          id: "commercial",
          label: "Commercial",
          path: "/contracts/default-terms",
          icon: HandshakeOutlinedIcon,
          keywords: ["contracts"],
          sections: [
            {
              id: "default-terms",
              label: "Default terms",
              path: "/contracts/default-terms",
              keywords: ["platform terms", "commission"],
            },
            {
              id: "fee-editions",
              label: "Fee editions",
              path: "/contracts/fee-policies",
              keywords: ["fee drafts", "fee policies", "stored fee terms"],
            },
            {
              id: "module-offers",
              label: "Module offers",
              path: "/contracts/module-offers",
              keywords: ["modules", "offers"],
            },
            {
              id: "acceptance-timeline",
              label: "Acceptance timeline",
              path: "/onboarding/acceptance",
              keywords: ["contract acceptance", "not accepted terms"],
            },
          ],
        },
      ],
    },
    {
      id: "ops",
      label: "Ops",
      icon: TuneOutlinedIcon,
      pages: [
        {
          id: "reservations",
          label: "Reservation lookup",
          path: "/reservations",
          icon: ManageSearchOutlinedIcon,
          keywords: ["reservations", "bookings"],
        },
        {
          id: "announcements",
          label: "Announcements",
          path: "/announcements",
          icon: CampaignOutlinedIcon,
        },
        {
          id: "compliance",
          label: "Compliance",
          path: "/compliance",
          icon: PrivacyTipOutlinedIcon,
          keywords: ["privacy", "deletion"],
        },
        {
          id: "audit",
          label: "Audit log",
          path: "/audit",
          icon: HistoryOutlinedIcon,
        },
        {
          id: "settings",
          label: "Settings",
          path: "/settings",
          icon: SettingsOutlinedIcon,
        },
      ],
    },
  ],
};

export const allowAll: AccessEvaluator<never> = () => true;
